import React, { useContext } from 'react';

import CheckIcon from 'mdi-react/CheckboxMarkedCircleIcon';
import ErrorIcon from 'mdi-react/CloseCircleIcon';

import { AppContext } from '@prisjakt/ui-app-context';
import { colors } from '@prisjakt/ui-base';
import Spinner from '@prisjakt/ui-spinner';
import StoreLogo from '@prisjakt/ui-store-logo';

import Modal from '../../../../modal';

import * as S from './styles';
import { StorePageQuery } from '../../../../../types';

type OwnProps = {
  closeModal: () => void;
  error: boolean;
  isOpen: boolean;
  priceValue: string;
  priceStore: StorePageQuery['store'];
  productImage: string;
  productName: string;
  reportWrongPrice: (e: React.MouseEvent) => void;
  reportWrongProduct: (e: React.MouseEvent) => void;
  reportWrongStockStatus: (e: React.MouseEvent) => void;
  sending: boolean;
  stockStatus: string;
  success: boolean;
};

type Props = OwnProps;

const ReportModal = ({
  closeModal,
  error,
  isOpen,
  priceValue,
  priceStore,
  productImage,
  productName,
  reportWrongPrice,
  reportWrongProduct,
  reportWrongStockStatus,
  sending,
  stockStatus,
  success,
}: Props) => {
  const { t } = useContext(AppContext);
  const submitted = success || error || sending;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon={!submitted}
    >
      <S.Content>
        {submitted ? (
          <S.SubmitMessage>
            {sending && <Spinner isActive position="none" size={30} />}

            {success && (
              <S.Message>
                <CheckIcon color={colors.green1} size="44" />
                {t('feedback', 'dialog.thanks-for-feedback')}
              </S.Message>
            )}
            {error && (
              <S.Message>
                <ErrorIcon color={colors.red1} size="44" />
                {t('feedback', 'dialog.request-error')}
              </S.Message>
            )}
          </S.SubmitMessage>
        ) : (
          <>
            <S.Title>
              {priceStore && priceStore.hasLogo && (
                <StoreLogo
                  name={priceStore.name}
                  storeId={priceStore.id}
                  featured={priceStore.featured}
                  hasImage={priceStore.hasLogo}
                  variant="PRICE_LIST"
                />
              )}
            </S.Title>

            <S.Card>
              <S.ProductImage src={productImage} name={productName} />
              <S.ProductName>{productName}</S.ProductName>
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-product-description')}
              </S.ReportDescription>
              <S.Button color="primary" onClick={reportWrongProduct} truncate>
                {t('feedback', 'report.wrong-product')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.PriceLabel value={priceValue} styling="primary" medium />
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-price-description')}
              </S.ReportDescription>

              <S.Button color="primary" onClick={reportWrongPrice} truncate>
                {t('feedback', 'report.wrong-price')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.StockStatus
                status={stockStatus}
                classicIcons
                showText={false}
                width={48}
                height={22}
              />
              <S.ReportDescription thin>
                {t('feedback', 'report.wrong-stock-status-description')}
              </S.ReportDescription>
              <S.Button
                color="primary"
                onClick={reportWrongStockStatus}
                truncate
              >
                {t('feedback', 'report.wrong-stock-status')}
              </S.Button>
            </S.Card>

            <S.Card>
              <S.ReportDescription>
                {t('feedback', 'report.other-feedback-description')}
              </S.ReportDescription>

              <S.FeedbackButton
                title={t('feedback', 'report.other-feedback')}
                variant="outlined"
              >
                <S.ButtonText>
                  {t('feedback', 'report.other-feedback')}
                </S.ButtonText>
              </S.FeedbackButton>
            </S.Card>
          </>
        )}
      </S.Content>
    </Modal>
  );
};

export default ReportModal;
